package com.royceecoleman.bill.shared.billing

import kotlinx.serialization.Serializable

/**
 * A rough note paired with the entry the client accepted. Profile examples are written by the lawyer (MANUAL);
 * the per-profile library also collects LEARNED examples from approved entries the lawyer edited.
 */
@Serializable
data class BillingExample(
    val before: String = "",
    val after: String = "",
    val taskCode: String = "",
    val activityCode: String = "",
    val id: String = "",
    val origin: String = "MANUAL",
    val createdAt: Long = 0,
)

/** Longest "About you" text a profile can hold. */
const val MAX_ABOUT_LENGTH = 4000

@Serializable
data class BillingProfile(
    val id: String = "",
    val revision: Int = 0,
    val name: String = "",
    /** The lawyer, in their own words: role, practice, who they bill and what those clients expect. Sent when drafting. */
    val about: String = "",
    val instructions: String = "",
    val examples: List<BillingExample> = emptyList(),
    val taskCodes: List<String> = BillingCodes.tasks.keys.toList(),
    val activityCodes: List<String> = BillingCodes.activities.keys.toList(),
    val incrementMinutes: Int = 6,
    val maxNarrativeLength: Int = 500,
    val prohibitedTerms: List<String> = emptyList(),
    /** Approved entries the lawyer rewrote become new examples for this profile. */
    val learnFromApprovals: Boolean = true,
    /**
     * Task and activity (UTBMS) codes on drafts, review and exports. Stored profiles saved before this switch have
     * no field and keep codes on; the workbench starts new profiles with codes off.
     */
    val includeCodes: Boolean = true,
)

/** A timekeeper on an invoice. The invoice's own timekeeper fields describe the default timekeeper. */
@Serializable
data class Timekeeper(
    val id: String = "",
    val name: String = "",
    val classification: String = "",
    val hourlyRate: String = "",
)

@Serializable
data class InvoiceDetails(
    val month: String = "",
    val clientId: String = "",
    val matterId: String = "",
    val clientMatterId: String = "",
    val clientHasNoMatterId: Boolean = false,
    val firmId: String = "",
    val timekeeperId: String = "",
    val timekeeperName: String = "",
    val timekeeperClassification: String = "",
    val hourlyRate: String = "",
    val currency: String = "USD",
    val invoiceNumber: String = "",
    val invoiceDate: String = "",
    /** LEDES INVOICE_DESCRIPTION; defaults to a professional-services description when blank. */
    val description: String = "",
    /** Timekeepers other than the default one; entries select them by ID. */
    val additionalTimekeepers: List<Timekeeper> = emptyList(),
    /** Optional YYYY-MM-DD for "all sources are from the same day"; a source's own date overrides it. */
    val workDate: String = "",
) {
    /** A source's or entry's own date, else the shared work date, else blank. */
    fun effectiveDate(date: String): String = date.ifBlank { workDate }
}

@Serializable
data class SourceNote(
    val id: String = "",
    val text: String = "",
    val date: String = "",
    val minutes: Int? = null,
    /** Blank means the invoice's default timekeeper. */
    val timekeeperId: String = "",
)

@Serializable
data class BillingEntry(
    val sourceId: String,
    val narrative: String = "",
    val date: String = "",
    val minutes: Int? = null,
    val taskCode: String = "",
    val activityCode: String = "",
    val aiWarnings: List<String> = emptyList(),
    val approved: Boolean = false,
    /** The AI's original draft, kept to measure how much the lawyer changed it. Blank for manual entries. */
    val draftNarrative: String = "",
    val draftTaskCode: String = "",
    val draftActivityCode: String = "",
    /** Blank means the invoice's default timekeeper. */
    val timekeeperId: String = "",
    /** Shown on the invoice at no charge (full write-down). */
    val noCharge: Boolean = false,
    /** Optional write-down in currency units, e.g. "50.00". */
    val discount: String = "",
)

@Serializable
data class ExpenseEntry(
    val id: String = "",
    val date: String = "",
    val description: String = "",
    val expenseCode: String = "",
    /** Positive decimal amount in the invoice currency. */
    val amount: String = "",
    val approved: Boolean = false,
)

@Serializable
data class ReviewIssue(
    val sourceId: String,
    val message: String,
    val blocking: Boolean = true,
    /** The entry's whole narrative with this issue fixed, when a wording check offers a replacement; otherwise empty. */
    val fix: String = "",
    /** The fix button's label, such as: Replace "regarding" with "re". */
    val fixLabel: String = "",
)

@Serializable
data class BillingAudit(
    val timestamp: Long,
    val action: String,
)

@Serializable
data class DraftProgress(
    val total: Int = 0,
    val drafted: Int = 0,
    val failed: Int = 0,
    /** Drafting is done and the batch is being checked against the standing rules. */
    val reviewing: Boolean = false,
)

@Serializable
data class BillingWorkspace(
    val id: String = "",
    val revision: Int = 0,
    val title: String = "",
    val profile: BillingProfile = BillingProfile(),
    val invoice: InvoiceDetails = InvoiceDetails(),
    val notes: List<SourceNote> = emptyList(),
    val entries: List<BillingEntry> = emptyList(),
    val status: String = "DRAFT",
    val styleSummary: String = "",
    val modelId: String = "",
    val inputTokens: Long = 0,
    val outputTokens: Long = 0,
    val updatedAt: Long = 0,
    val error: String = "",
    val audit: List<BillingAudit> = emptyList(),
    val issues: List<ReviewIssue> = emptyList(),
    val expenses: List<ExpenseEntry> = emptyList(),
    val clientRecordId: String = "",
    val matterRecordId: String = "",
    /** Identifies the running draft job so stale background results can never overwrite newer work. */
    val generation: String = "",
    val progress: DraftProgress = DraftProgress(),
    /** Credits held for the running draft; unused credits are returned when it finishes. */
    val reservedCredits: Int = 0,
    val creditBucket: String = "",
) {
    /** Entries without a work date or recorded time, e.g. "entry 2 (time)". CSV exports them with blanks; LEDES cannot. */
    fun incompleteEntries(): List<String> =
        entries.mapNotNull { entry ->
            val missing =
                listOfNotNull(
                    "date".takeIf { invoice.effectiveDate(entry.date).isBlank() },
                    "time".takeIf { entry.minutes == null },
                )
            if (missing.isEmpty()) null else "entry ${entry.sourceId} (${missing.joinToString(" and ")})"
        }
}

@Serializable
data class WorkspaceSummary(
    val id: String,
    val title: String,
    val month: String,
    val status: String,
    val entries: Int,
    val approved: Int,
    val updatedAt: Long,
)

@Serializable
data class WorkspaceCreate(
    val requestId: String,
    val title: String,
    val profileId: String,
    val invoice: InvoiceDetails,
    val notes: List<SourceNote>,
    val clientRecordId: String = "",
    val matterRecordId: String = "",
)

@Serializable
data class WorkspaceEdit(
    val revision: Int,
    val invoice: InvoiceDetails,
    val entries: List<BillingEntry>,
    /** When present, replaces the source notes (added, split, merged or removed work). Entries must match them. */
    val notes: List<SourceNote>? = null,
    val expenses: List<ExpenseEntry>? = null,
)

@Serializable
data class RevisionRequest(
    val revision: Int,
)

@Serializable
data class RedraftRequest(
    val revision: Int,
    /** Optional guidance from the lawyer, e.g. "mention the deposition date". */
    val hint: String = "",
)

@Serializable
data class BulkApproveRequest(
    val revision: Int,
    /** Entries to approve; empty approves every entry and expense without blocking issues. */
    val sourceIds: List<String> = emptyList(),
)

@Serializable
data class BulkApproveResult(
    val workspace: BillingWorkspace,
    val approved: Int,
    val skipped: Int,
)

@Serializable
data class BillingAccess(
    val plan: String = "TRIAL",
    val remaining: Int = 0,
    val limit: Int = 20,
    val resetDate: String = "",
    val paymentsConfigured: Boolean = false,
    val hasCustomer: Boolean = false,
    val description: String = "",
    /** Administrator-added credits included in [remaining]; they do not reset. */
    val addedCredits: Int = 0,
)

/** How closely approved entries matched the AI drafts for one profile. */
@Serializable
data class ProfileStats(
    val profileId: String,
    val approvedDrafts: Int = 0,
    val unchanged: Int = 0,
    val meanSimilarity: Double = 0.0,
    val taskCodeKept: Int = 0,
    val activityCodeKept: Int = 0,
    val learnedExamples: Int = 0,
)

@Serializable
data class BillingMatter(
    val id: String = "",
    val name: String = "",
    val matterId: String = "",
    val clientMatterId: String = "",
    val clientHasNoMatterId: Boolean = false,
    /** Optional matter-specific rate for the default timekeeper. */
    val hourlyRate: String = "",
    val profileId: String = "",
)

/** Reusable client defaults so each month's workspace does not retype IDs, rates and timekeepers. */
@Serializable
data class BillingClient(
    val id: String = "",
    val revision: Int = 0,
    val name: String = "",
    val clientId: String = "",
    val firmId: String = "",
    val currency: String = "USD",
    val defaultProfileId: String = "",
    /** The first timekeeper is the default on new invoices. */
    val timekeepers: List<Timekeeper> = emptyList(),
    val matters: List<BillingMatter> = emptyList(),
)

@Serializable
data class WorkspaceHome(
    val profiles: List<BillingProfile> = emptyList(),
    val workspaces: List<WorkspaceSummary> = emptyList(),
    val access: BillingAccess = BillingAccess(),
    val clients: List<BillingClient> = emptyList(),
    val profileStats: List<ProfileStats> = emptyList(),
)

@Serializable
data class BillingDownload(
    val filename: String,
    val contentType: String,
    val content: String,
)

@Serializable
data class BillingLink(
    val url: String,
)

/** A timekeeping export (.csv, .tsv, .txt or .xlsx) to turn into source notes for confirmation. */
@Serializable
data class NoteImportRequest(
    val filename: String,
    val contentBase64: String,
    /** Billing month (YYYY-MM) used to complete dates written without a year. */
    val month: String = "",
)

@Serializable
data class NoteImportResult(
    val notes: List<SourceNote> = emptyList(),
    val warnings: List<String> = emptyList(),
)

/** ABA litigation task / activity identifiers and ABA expense codes. Client-approved subsets live in each profile. */
object BillingCodes {
    val tasks =
        linkedMapOf(
            "L110" to "Fact investigation",
            "L120" to "Analysis and strategy",
            "L130" to "Experts / consultants",
            "L140" to "Document management",
            "L150" to "Budgeting",
            "L160" to "Settlement / non-binding ADR",
            "L190" to "Other case assessment",
            "L210" to "Pleadings",
            "L220" to "Preliminary injunctions",
            "L230" to "Court-mandated conferences",
            "L240" to "Dispositive motions",
            "L250" to "Other written motions",
            "L260" to "Class certification",
            "L310" to "Written discovery",
            "L320" to "Document production",
            "L330" to "Depositions",
            "L340" to "Expert discovery",
            "L350" to "Discovery motions",
            "L390" to "Other discovery",
            "L410" to "Fact witnesses",
            "L420" to "Expert witnesses",
            "L430" to "Written motions / submissions",
            "L440" to "Other trial preparation",
            "L450" to "Trial / hearing",
            "L460" to "Post-trial motions",
            "L470" to "Enforcement",
            "L510" to "Appellate motions",
            "L520" to "Appellate briefs",
            "L530" to "Oral argument",
        )
    val activities =
        linkedMapOf(
            "A101" to "Plan / prepare",
            "A102" to "Research",
            "A103" to "Draft / revise",
            "A104" to "Review / analyze",
            "A105" to "Communicate within firm",
            "A106" to "Communicate with client",
            "A107" to "Communicate with other outside counsel",
            "A108" to "Other external communication",
            "A109" to "Appear / attend",
            "A110" to "Manage data / files",
            "A111" to "Other",
        )
    val expenses =
        linkedMapOf(
            "E101" to "Copying",
            "E102" to "Outside printing",
            "E103" to "Word processing",
            "E104" to "Facsimile",
            "E105" to "Telephone",
            "E106" to "Online research",
            "E107" to "Delivery services / messengers",
            "E108" to "Postage",
            "E109" to "Local travel",
            "E110" to "Out-of-town travel",
            "E111" to "Meals",
            "E112" to "Court fees",
            "E113" to "Subpoena fees",
            "E114" to "Witness fees",
            "E115" to "Deposition transcripts",
            "E116" to "Trial transcripts",
            "E117" to "Trial exhibits",
            "E118" to "Litigation support vendors",
            "E119" to "Experts",
            "E120" to "Private investigators",
            "E121" to "Arbitrators / mediators",
            "E122" to "Local counsel",
            "E123" to "Other professionals",
            "E124" to "Other",
        )
}
